import type { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder/types'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import type { EntityClassName } from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import {
  canonicalizeFilter,
  savedFilterStructureSchema,
} from '@/Atomic/Widgets/AiChatBot/core/tools/filter-normalizer'
import { type ScrivitoAccessor } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'
import { DataClassName } from '@/Data/const'

// Bound the candidate read: one base entity's dynamic segments, newest first. Far
// above any realistic count — the canonical compare is cheap, but loading records is
// not, so cap what we pull. A user with more than this many dynamic segments of one
// entity is not a real scenario; if it ever were, the guard degrades to "no duplicate
// found" (a missed warning), never a wrong one.
const CANDIDATE_CAP = 200

export interface SegmentRef {
  id: string
  title?: string
  url?: string
}

export interface SegmentCollisions {
  /** A dynamic segment with the EXACT same filter (order-independent). */
  match: SegmentRef | null
  /** A dynamic segment with the SAME name but a DIFFERENT filter. */
  nameMatch: SegmentRef | null
}

/**
 * Detects whether a dynamic segment that collides with the proposed one already
 * exists — by filter OR by name. Shared by find_matching_segment (build_filter
 * pre-check) and create_segment (write-boundary guard) so there is ONE dedup
 * implementation and the guard covers every create path, including the applied-filter
 * save where the model has no structure to pre-check with.
 *
 * NOT scoped to the current user: segments are instance-wide — the app shows a single
 * shared "Segments" list everyone sees, so a duplicate is a duplicate regardless of
 * who owns it.
 */
export async function findSegmentCollisions(
  entityType: EntityClassName,
  structure: SavedFilterStructure,
  name?: string,
): Promise<SegmentCollisions> {
  const { data, navigation: nav } = getHostRuntime()
  const target = JSON.stringify(canonicalizeFilter(structure))

  const filters: Record<string, string | number | boolean | null> = {
    entityType,
    type: 'dynamic',
  }
  const order: [string, 'asc' | 'desc'][] = [['updatedAt', 'desc']]

  // data.query wraps take() in a single load(); reading .get() off the loaded items
  // mirrors the manual segment list. `query` is the List's untyped membership-query
  // passthrough.
  const items = (await data.query(DataClassName.LIST, {
    filters,
    order,
    limit: CANDIDATE_CAP,
  })) as ScrivitoAccessor[]
  const candidates = items.map((item) => ({
    id: item.id(),
    title: item.get('title'),
    query: item.get('query') as Record<string, unknown> | null,
  }))

  const match = candidates.find((c) => {
    const parsed = savedFilterStructureSchema.safeParse(c.query?.filters)
    if (!parsed.success) {
      return false
    }
    return JSON.stringify(canonicalizeFilter(parsed.data)) === target
  })

  // Same-name collision: a segment sharing the intended name but NOT the exact filter
  // (an exact-filter match is the stronger signal and reported as `match`). Compared
  // case-insensitively on the trimmed title, mirroring how a user reads the list.
  const wantedName = name?.trim().toLowerCase()
  const nameMatch = wantedName
    ? candidates.find(
        (c) =>
          c.id !== match?.id &&
          typeof c.title === 'string' &&
          c.title.trim().toLowerCase() === wantedName,
      )
    : undefined

  // Resolve a segment's open URL — best-effort.
  const resolveUrl = async (id: string) => {
    const item = await data.get(DataClassName.LIST, id).catch(() => undefined)
    if (!item) return undefined
    return nav.urlFor(item)
  }

  const toResult = async (
    c: (typeof candidates)[number],
  ): Promise<SegmentRef> => {
    const url = await resolveUrl(c.id)
    return {
      id: c.id,
      ...(typeof c.title === 'string' && c.title.length > 0
        ? { title: c.title }
        : {}),
      ...(url ? { url } : {}),
    }
  }

  return {
    match: match ? await toResult(match) : null,
    nameMatch: nameMatch ? await toResult(nameMatch) : null,
  }
}
